import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from '@shared/stores/rootTypes';
import type { BuilderType } from '@shared/utils/api/configs/rtkBaseQueryDemo';
import { GET_INBOX_MESSAGES_URL } from '@shared/utils/api/configs/URL';
import { hasCcdpSuccess } from '@shared/utils/api/handlers/interpretCcdpCode/interpretCcdpCode';
import type { GenericApiResponse } from '@shared/utils/api/types';

import type { GetInboxMessagesRequestPayload, GetInboxMessagesResult } from '../types/getInboxMessages.types';

export const INBOX_MESSAGES_TAGS = 'getInboxMessages';

export const getInboxMessages = (builder: BuilderType) =>
  builder.query<GetInboxMessagesResult, GetInboxMessagesRequestPayload>({
    merge: (currentCacheData, responseData, { arg }) => {
      if (arg.forceReset) {
        currentCacheData.MessageList = responseData.MessageList ?? [];
      } else if (responseData.MessageList && arg.isLoadMore) {
        currentCacheData.MessageList.push(...responseData.MessageList);
      }
      currentCacheData.HasMoreRecords = responseData.HasMoreRecords;
      currentCacheData.Offset = responseData.Offset;
    },
    providesTags: [INBOX_MESSAGES_TAGS],
    query: (data) => ({
      data: {
        ...data,
        isLoadMore: undefined,
        forceReset: undefined,
      },
      method: 'POST',
      url: GET_INBOX_MESSAGES_URL,
    }),
    serializeQueryArgs: ({ queryArgs }) => ({
      ...queryArgs,
      isLoadMore: undefined,
      forceReset: undefined,
      Offset: undefined,
    }),
    transformResponse: (response: GenericApiResponse<GetInboxMessagesResult>) => {
      if (hasCcdpSuccess(response)) {
        return response.Result;
      }

      throw {
        code: response.Code,
        message: response.Message,
      };
    },
  });

export const selectInboxCacheEntries = createSelector(
  (state: RootState) => state.baseApi.queries,
  (queries) =>
    Object.values(queries).flatMap((entry) =>
      entry?.endpointName === INBOX_MESSAGES_TAGS && entry.originalArgs
        ? [entry.originalArgs as GetInboxMessagesRequestPayload]
        : []
    )
);
